import { useErrorHandlingSubscription } from '@backoffice-frontend/data-fetching'
import { Divider } from '@backoffice-frontend/patterns'
import { useTheme } from '@emotion/react'
import { ArrowDownIcon } from '@moia-dev/pace-icons'
import { motion } from 'framer-motion'
import { AdSeatIndicator } from './AdSeatIndicator'
import { useAdSeatStatusSubscription } from './AdSeatStatus.hook'

export const AdSeatStatus = ({ vehicleId }: { vehicleId: string }) => {
  const theme = useTheme()

  const telemetrySubscription = useErrorHandlingSubscription(
    useAdSeatStatusSubscription,
    {
      variables: { vehicleId },
      fetchPolicy: 'no-cache',
      onError: e => {
        console.error('Error in subscription', e)
      },
    },
  )

  const telemetry = telemetrySubscription.data?.fccVehicleTelemetry

  const isDoorTelemetryUnavailable =
    telemetry?.rightSlidingDoorOpen === undefined ||
    telemetry?.rightSlidingDoorOpen === null

  return (
    <div
      css={{
        position: 'relative',
        width: 144,
        height: 250,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        borderRadius: '8px 8px 40px 40px',
        backgroundColor: theme.semantic.ColorBackgroundAlternative,
      }}
    >
      {telemetry && (
        <div
          css={{
            width: 1,
            height: 80,
            position: 'absolute',
            top: '25%',
            left: -1, // Covering the vehicle's outline to create the door opening
            backgroundColor: theme.semantic.ColorBackgroundDefault,
          }}
        >
          <motion.div
            css={{
              width: 3,
              height: 76,
              borderRadius: 2,
            }}
            hidden={isDoorTelemetryUnavailable}
            initial={false}
            aria-label="door-indicator"
            animate={
              telemetry.rightSlidingDoorOpen
                ? {
                    x: [-1, -5, -5],
                    y: [2, 2, -50],
                    backgroundColor: [
                      theme.semantic.ColorSuccessDefault,
                      theme.semantic.ColorWarningDefault,
                      theme.semantic.ColorWarningDefault,
                    ],
                  }
                : {
                    x: [-5, -5, -1],
                    y: [-50, 2, 2],
                    backgroundColor: [
                      theme.semantic.ColorWarningDefault,
                      theme.semantic.ColorWarningDefault,
                      theme.semantic.ColorSuccessDefault,
                    ],
                  }
            }
            transition={{
              duration: 1,
              times: [0, 0.2, 1],
              ease: 'easeInOut',
            }}
          />
        </div>
      )}
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
        }}
      >
        <AdSeatIndicator type={telemetry?.seatStatus?.thirdRowRight} />
        <AdSeatIndicator type={telemetry?.seatStatus?.thirdRowMiddle} />
        <AdSeatIndicator type={telemetry?.seatStatus?.thirdRowLeft} />
      </div>

      <Divider title="3" />

      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
        }}
      >
        <AdSeatIndicator type={telemetry?.seatStatus?.secondRowRight} />
        <AdSeatIndicator type={telemetry?.seatStatus?.secondRowMiddle} />
        <AdSeatIndicator type={telemetry?.seatStatus?.secondRowLeft} />
      </div>

      <Divider title="2" />

      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
        }}
      >
        <AdSeatIndicator type="LUGGAGE" />
        <AdSeatIndicator />
        <AdSeatIndicator type="DRIVER" />
      </div>

      <Divider title="1" />

      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ArrowDownIcon color={theme.semantic.ColorAccessoryDefault} />
      </div>
    </div>
  )
}
